import PropTypes from 'prop-types';
import { useState, useCallback, useEffect } from 'react';
import { useI18n } from '../../../../context/I18nContext';
import { useRoomCreate } from '../../../../hooks/useRoomCreate';
import { useAvatar } from '../../../../context/AvatarContext';
import { useMatrixClient } from '../../../../context/MatrixContext';
import logger from '../../../../utils/logger';
import TopBar from './TopBar';
import ContactsSelector from './ContactsSelector';
import Messages from './Messages/Messages';
import './NewChatView.css';

const NewChatView = ({
  onSave = () => {},
  onCancel = () => {},
  isChannel = false,
}) => {
  const { t } = useI18n();
  const client = useMatrixClient();
  const { getFallbackAvatarsFromMembers } = useAvatar();

  // Hook to manage contact selection state and avatar (for new room creation)
  const {
    selectedContacts,
    getDefaultTitle,
    toggleContact,
    removeContact,
    resetToOriginal,
    maxSelections,
    selectedAvatarOption,
    selectedAvatarFile,
    selectDefaultAvatar,
    selectPresetAvatar,
    selectCustomAvatar,
    currentUser,
    canCreate,
    topic,
    setTopic,
    externalUsers,
  } = useRoomCreate({
    maxSelections: 1,
    minParticipantsSelected: 0,
    allowEmptyRoom: isChannel,
  });

  const [title, setTitle] = useState('');
  const [initialMessageText, setInitialMessageText] = useState('');
  const [avatarUrls, setAvatarUrls] = useState([]);
  const [defaultAvatarUrls, setDefaultAvatarUrls] = useState([]);
  const [isPrivateRoom, setIsPrivateRoom] = useState(true);

  const defaultTitle = getDefaultTitle();
  const myUserId = client?.getUserId();

  // Compute member avatars (including current user) for display
  useEffect(() => {
    const computeAvatars = async () => {
      if (!client || !myUserId || !currentUser) return;

      // Build members array - ALWAYS include current user
      const members = [];
      
      // Add current user first
      members.push({
        userId: myUserId,
        name: currentUser.displayName || myUserId,
        rawDisplayName: currentUser.rawDisplayName || currentUser.displayName || myUserId,
      });
      
      // Add selected contacts
      selectedContacts.forEach(contact => {
        members.push({
          userId: contact.id,
          name: contact.name,
          rawDisplayName: contact.name,
        });
      });

      if (members.length > 0) {
        const fallbacks = await getFallbackAvatarsFromMembers(members, 96);
        const urls = fallbacks.map(desc => desc.url).filter(Boolean);
        setAvatarUrls(urls);
        setDefaultAvatarUrls(urls);
      } else {
        setAvatarUrls([]);
        setDefaultAvatarUrls([]);
      }
    };

    computeAvatars();
  }, [client, myUserId, currentUser, selectedContacts, getFallbackAvatarsFromMembers]);

  const handleCancel = useCallback(() => {
    setInitialMessageText('');
    setTitle('');
    resetToOriginal();
    onCancel();
  }, [onCancel, resetToOriginal]);

  const handleSendMessage = useCallback(() => {
    // onSave is triggered by the TopBar save button
  }, []);

  const onSaveClick = () => {
    logger.debug('[NewChatView] onSaveClick - User clicked save button');
    const finalTitle = (title.trim() !== '') ? title : undefined;
    const conversationData = {
      title: finalTitle,
      topic: topic.trim(),
      selectedContacts,
      initialMessageText: initialMessageText.trim(),
      selectedAvatarOption,
      selectedAvatarFile,
      isPublicRoom: isChannel ? !isPrivateRoom : undefined, // Only include for channels
    };
    logger.debug('[NewChatView] Calling onSave with conversationData:', {
      title: conversationData.title,
      topic: conversationData.topic,
      selectedContactsCount: conversationData.selectedContacts.length,
      selectedContactIds: conversationData.selectedContacts.map(c => c.id),
      hasInitialMessage: !!conversationData.initialMessageText,
      avatarOption: conversationData.selectedAvatarOption,
      isPublicRoom: conversationData.isPublicRoom,
    });
    onSave(conversationData);
  };

  return (
    <div className="new-chat-view">
      <div className="new-chat-top-bar-container">
        <TopBar
          title={title}
          topic={topic}
          isEditMode={true}
          setTitle={setTitle}
          setTopic={setTopic}
          defaultTitle={defaultTitle}
          defaultTopic='Set a Topic'
          onSaveClick={onSaveClick}
          onCancelClick={handleCancel}
          isCreating={true}
          canSave={canCreate}
          selectedAvatarOption={selectedAvatarOption}
          selectedAvatarFile={selectedAvatarFile}
          avatarUrls={avatarUrls}
          defaultAvatarUrls={defaultAvatarUrls}
          selectDefaultAvatar={selectDefaultAvatar}
          selectPresetAvatar={selectPresetAvatar}
          selectCustomAvatar={selectCustomAvatar}
        />
      </div>
      <div className="new-chat-content">
        <div className="new-chat-messages-container">
          <Messages
            roomId={null}
            messages={[]}
            placeholder={t('ChatViewFooter.sendMessagePlaceholder')}
            disabled={false}
            value={initialMessageText}
            onSetMessage={setInitialMessageText}
            onSendMessage={handleSendMessage}
          />
        </div>
        <div className="new-chat-contacts-container">
          <ContactsSelector
            selectedContacts={selectedContacts}
            onRemoveContact={removeContact}
            onToggleContact={toggleContact}
            maxSelections={maxSelections}
            showRoomTypeToggle={isChannel}
            isPrivateRoom={isPrivateRoom}
            onRoomTypeChange={setIsPrivateRoom}
            additionalUsers={externalUsers}
          />
        </div>
      </div>
    </div>
  );
};

NewChatView.propTypes = {
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  isChannel: PropTypes.bool,
};

export default NewChatView;
